import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useRouter, createFileRoute, redirect } from "@tanstack/react-router";
import { MoreVerticalIcon } from "lucide-react";
import * as React from "react";

import { BlueskyIcon } from "@/components/BlueskyIcon";
import { Page } from "@/components/Page";
import { PreviewPost } from "@/components/PreviewFeed";
import { RejectCampaignDialog } from "@/components/RejectCampaignDialog";
import { Spinner } from "@/components/Spinner";
import { FeedAvatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@/components/ui/menu";
import { Anchor, Blockquote } from "@/components/ui/typography";
import {
  acceptCampaign,
  campaignQueryOptions,
  getHydratedPostQueryOptions,
  sponsoredPostsPerformanceQueryOptions,
} from "@/lib/api";
import { Campaign, CampaignStatus, Feed } from "@/lib/types";
import { atUriToBskyUrl } from "@/lib/utils";

function ReviewPostPreview({
  uri,
  actions,
}: {
  uri: string;
  actions?: React.ReactNode;
}) {
  const { data: post, isPending } = useQuery(getHydratedPostQueryOptions(uri));

  if (isPending || !post) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return <PreviewPost record={post} actions={actions} />;
}

export const Route = createFileRoute(
  "/app/_sidebar-layout/campaigns/$campaignId/review/$algoId",
)({
  component: RouteComponent,
  loader: async ({
    context: { queryClient },
    params: { campaignId, algoId },
  }) => {
    const campaign = await queryClient.ensureQueryData(
      campaignQueryOptions(parseInt(campaignId)),
    );
    const algo = campaign.algorithms.find((a) => a.id === parseInt(algoId))!;

    if (algo.details.state !== "proposed") {
      if (algo.details.state === "denied") {
        throw redirect({ to: "/app/campaigns" });
      } else {
        throw redirect({
          to: `/app/campaigns/$campaignId/feed/$algoId`,
          params: { campaignId, algoId },
        });
      }
    }

    void Promise.all([
      queryClient.ensureQueryData(
        sponsoredPostsPerformanceQueryOptions(
          parseInt(campaignId),
          parseInt(algoId),
        ),
      ),
    ]);
  },
});

function ProposalReviewPage({
  campaign,
  algo,
}: {
  campaign: Campaign;
  algo: Feed & {
    details: CampaignStatus;
  };
}) {
  const router = useRouter();
  const { campaignId } = Route.useParams();
  const acceptCampaignMutation = useMutation({
    onSuccess: () => router.history.back(),
    mutationFn: () => acceptCampaign(parseInt(campaignId), algo.id),
  });

  return (
    <Page>
      <div className="-mx-4 w-[calc(100%+32px)] md:max-w-xl md:w-full flex flex-col md:mx-auto md:bg-slate-subtle rounded-lg md:border border-slate-6 dark:border-slatedark-6">
        <div className="p-4 border-b border-slate-6 dark:border-slatedark-6">
          <div className="flex items-center justify-between gap-8 self-stretch">
            <div className="flex flex-col gap-3 self-stretch">
              <div className="flex items-center gap-4">
                <FeedAvatar feed={algo} className="h-[52px] w-[52px]" />
                <div className="flex flex-col gap-0.5">
                  <h1 className="text-2xl font-semibold">
                    {campaign.display_name} Campaign Proposal
                  </h1>
                  <div className="text-sm text-slate-dim">
                    {algo.display_name}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-sm p-5 border-b border-slate-6 dark:border-slatedark-6 flex flex-col gap-3">
          {algo.details.metadata?.reason && (
            <Blockquote>{algo.details.metadata.reason}</Blockquote>
          )}

          <div>
            <span className="text-slate-dim font-semibold">From: </span>
            <Anchor
              href={`https://bsky.app/profile/${campaign.user.handle}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              @{campaign.user.handle}
            </Anchor>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 border-b border-slate-6 dark:border-slatedark-6 p-4">
          <div className="flex flex-col items-start gap-2 bg-slate-3 dark:bg-slatedark-2 rounded-lg p-4">
            <div className="md:text-xl font-semibold">
              ${((algo?.cpm_cents || 0) / 100).toFixed(2)}
            </div>
            <div className="text-slate-dim text-xs">CPM</div>
          </div>
          <div className="flex flex-col items-start gap-2 bg-slate-3 dark:bg-slatedark-2 rounded-lg p-4">
            <div className="md:text-xl font-semibold">
              {algo?.details.impressions_budget}
            </div>
            <div className="text-slate-dim text-xs">Impressions</div>
          </div>
          <div className="flex flex-col items-start gap-2 bg-slate-3 dark:bg-slatedark-2 rounded-lg p-4">
            <div className="md:text-xl font-semibold">
              $
              {(
                ((algo?.cpm_cents || 0) / 100 / 1000) *
                (algo?.details.impressions_budget || 0)
              ).toFixed(2)}
            </div>
            <div className="text-slate-dim text-xs">Order</div>
          </div>
          {algo.details.days_to_completion && (
            <div className="flex flex-col items-start gap-2 bg-slate-3 dark:bg-slatedark-2 rounded-lg p-4">
              <div className="md:text-xl font-semibold">
                {algo?.details.days_to_completion} days
              </div>
              <div className="text-slate-dim text-xs">Duration</div>
            </div>
          )}
        </div>

        <div className="max-h-[80vh] overflow-auto border-b border-slate-6 dark:border-slatedark-6 p-4">
          {campaign.sticky_posts.map((post) => (
            <ReviewPostPreview
              key={post.id}
              uri={post.uri}
              actions={
                <DropdownMenu>
                  <MenuTrigger asChild>
                    <Button variant="ghost" size="smIcon">
                      <span className="sr-only">Post actions</span>
                      <MoreVerticalIcon className="h-4 w-4" />
                    </Button>
                  </MenuTrigger>
                  <MenuContent>
                    <MenuItem asChild={true}>
                      <a
                        className="flex items-center gap-1"
                        href={atUriToBskyUrl(post.uri)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <BlueskyIcon />
                        View on Bluesky
                      </a>
                    </MenuItem>
                  </MenuContent>
                </DropdownMenu>
              }
            />
          ))}
        </div>

        <div className="flex justify-end gap-4 p-4">
          <RejectCampaignDialog
            type="reject"
            campaignId={parseInt(campaignId)}
            feedId={algo.id}
            onSuccess={() => router.history.back()}
          >
            <Button variant="outline" className="flex-1" type="button">
              Reject
            </Button>
          </RejectCampaignDialog>
          <Button
            onPress={() => acceptCampaignMutation.mutate()}
            className="flex-1"
            type="button"
          >
            Accept
          </Button>
        </div>
      </div>
    </Page>
  );
}

function RouteComponent() {
  const { campaignId, algoId } = Route.useParams();
  const { data: campaign } = useSuspenseQuery(
    campaignQueryOptions(parseInt(campaignId)),
  );
  const algo = campaign.algorithms.find((a) => a.id === parseInt(algoId))!;

  return <ProposalReviewPage algo={algo} campaign={campaign} />;
}
